import React, { useState } from "react";
import { Mail, Phone, MapPin, AlertCircle, Copy, Check } from "lucide-react";
import { brand } from "../mock";
import { useContent, renderHighlightedTitle } from "../lib/useContent";
import { PageSEO } from "../components/PageSEO";

const Contact = () => {
  const [copied, setCopied] = useState(null);
  const { field, seo } = useContent("contact");
  const heroPretitle = field("hero", "pretitle", "Contatti");
  const heroHeadline = field("hero", "headline", "Scriviamo insieme la prossima pagina di Sanluri.");
  const heroHighlight = field("hero", "highlight_terms", ["insieme"]);
  const heroDescription = field("hero", "description", "Vuoi associare la tua attività, proporre un evento o farci una domanda? Usa i canali qui sotto: rispondiamo entro un giorno lavorativo.");

  const copy = async (label, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      setTimeout(() => setCopied(null), 1800);
    } catch (err) {
      console.warn("Clipboard copy failed", err);
    }
  };

  const preventFakeSubmit = (e) => {
    e.preventDefault();
    // Guard: no fake success. The form is intentionally not wired yet.
    // Users are guided to email / phone instead.
  };

  return (
    <div className="pt-32">
      <PageSEO seo={seo} defaults={{ title: "Contatti — CCN Sanluri", description: "Scrivici, chiamaci o passa in segreteria: rispondiamo entro un giorno lavorativo." }} />
      <section className="pb-8">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          {heroPretitle !== "" && <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-6">{heroPretitle}</div>}
          <h1 className="font-display font-bold text-5xl md:text-7xl leading-[1.02] tracking-tight text-[#1F2937] max-w-4xl">
            {renderHighlightedTitle(heroHeadline, heroHighlight)}
          </h1>
          {heroDescription !== "" && (
            <p className="mt-8 max-w-2xl text-lg text-slate-600">{heroDescription}</p>
          )}
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="bg-stone-950 text-white rounded-[32px] p-10 h-full relative overflow-hidden">
              <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full" style={{ background: "radial-gradient(circle, rgba(18,150,74,0.6) 0%, rgba(18,150,74,0) 70%)" }} />
              <div className="relative">
                <h3 className="font-display font-bold text-3xl md:text-4xl mb-8">Informazioni</h3>
                <ul className="space-y-6">
                  <li className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-full bg-white/10 border border-white/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-4 h-4 text-[#6FC247]" />
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-[0.2em] text-slate-400">Indirizzo</div>
                      <div className="mt-1">{brand.address}</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-full bg-white/10 border border-white/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-4 h-4 text-[#6FC247]" />
                    </div>
                    <div className="flex-1">
                      <div className="text-xs uppercase tracking-[0.2em] text-slate-400">Telefono</div>
                      <a href={`tel:${brand.phone}`} className="mt-1 hover:text-[#6FC247] block">{brand.phone}</a>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-full bg-white/10 border border-white/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-4 h-4 text-[#6FC247]" />
                    </div>
                    <div className="flex-1">
                      <div className="text-xs uppercase tracking-[0.2em] text-slate-400">Email</div>
                      <div className="mt-1 flex items-center gap-3">
                        <a href={`mailto:${brand.email}`} className="hover:text-[#6FC247]">{brand.email}</a>
                        <button
                          type="button"
                          onClick={() => copy("email", brand.email)}
                          className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1"
                        >
                          {copied === "email" ? <><Check className="w-3 h-3" /> Copiato</> : <><Copy className="w-3 h-3" /> Copia</>}
                        </button>
                      </div>
                    </div>
                  </li>
                </ul>
                <div className="mt-12 pt-10 border-t border-white/10">
                  <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-3">Orari segreteria</div>
                  <div>Lunedì – Venerdì</div>
                  <div>09:00 – 13:00 · 16:00 – 19:00</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-white rounded-[32px] p-10 border border-[#E5E7EB]">
              <div className="mb-6 flex items-start gap-3 bg-[#FFF9C4] border border-[#F4E500] text-[#1F2937] rounded-2xl px-5 py-4">
                <AlertCircle className="w-5 h-5 mt-0.5 text-[#12964A] flex-shrink-0" />
                <div className="text-sm leading-relaxed">
                  <strong>Modulo di contatto in attivazione.</strong> La ricezione
                  automatica dei messaggi via form non è ancora attiva. Nel
                  frattempo scrivici a
                  <a href={`mailto:${brand.email}`} className="text-[#12964A] underline ml-1">{brand.email}</a>{" "}
                  o chiama il numero indicato: rispondiamo in giornata.
                </div>
              </div>

              <h3 className="font-display font-bold text-3xl md:text-4xl mb-6 text-[#1F2937]">Contattaci direttamente</h3>
              <p className="text-slate-600 mb-8">
                Scegli il canale che preferisci. Rispondiamo entro un giorno
                lavorativo dal lunedì al venerdì.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <a href={`mailto:${brand.email}`} className="group flex items-start gap-4 border border-[#E5E7EB] hover:border-[#12964A] rounded-2xl p-5 transition-colors">
                  <div className="w-10 h-10 rounded-xl bg-[#F0FAF3] text-[#12964A] flex items-center justify-center">
                    <Mail className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Email</div>
                    <div className="mt-1 font-medium text-[#1F2937] group-hover:text-[#12964A]">{brand.email}</div>
                  </div>
                </a>
                <a href={`tel:${brand.phone}`} className="group flex items-start gap-4 border border-[#E5E7EB] hover:border-[#12964A] rounded-2xl p-5 transition-colors">
                  <div className="w-10 h-10 rounded-xl bg-[#F0FAF3] text-[#12964A] flex items-center justify-center">
                    <Phone className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Telefono</div>
                    <div className="mt-1 font-medium text-[#1F2937] group-hover:text-[#12964A]">{brand.phone}</div>
                  </div>
                </a>
              </div>

              <form onSubmit={preventFakeSubmit} className="mt-10 opacity-60 pointer-events-none select-none" aria-hidden="true">
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">Anteprima form (non attivo)</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input disabled placeholder="Nome" className="w-full bg-[#F8F9FA] border border-[#E5E7EB] rounded-2xl px-4 py-3.5" />
                  <input disabled placeholder="Email" className="w-full bg-[#F8F9FA] border border-[#E5E7EB] rounded-2xl px-4 py-3.5" />
                </div>
                <textarea disabled rows={3} placeholder="Messaggio..." className="w-full mt-4 bg-[#F8F9FA] border border-[#E5E7EB] rounded-2xl px-4 py-3.5" />
              </form>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="rounded-[32px] overflow-hidden aspect-[21/9] border border-[#E5E7EB]">
            <iframe
              title="Mappa Sanluri"
              src="https://www.openstreetmap.org/export/embed.html?bbox=8.895%2C39.55%2C8.945%2C39.585&layer=mapnik&marker=39.5666%2C8.9"
              className="w-full h-full"
              loading="lazy"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
